import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getCurrentUser } from "@/lib/apiClient";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft,
  Package,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  MapPin,
  Calendar,
  DollarSign,
  ArrowUpCircle,
  ArrowDownCircle,
  History,
  Plus,
  Minus,
  RotateCcw,
  Truck,
} from "lucide-react";
import { StockMovementDialog } from "./StockMovementDialog";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface MaterialDetailsProps {
  materialId: string;
  onBack: () => void;
}

interface InventoryMovement {
  id: string;
  material_inventory_id: string;
  movement_type: string;
  quantity: number;
  reason: string | null;
  reference_type: string | null;
  reference_id: string | null;
  performed_by: string | null;
  notes: string | null;
  previous_stock: number | null;
  new_stock: number | null;
  created_at: string;
  profiles?: {
    full_name: string | null;
    email: string;
  };
}

export function MaterialDetails({ materialId, onBack }: MaterialDetailsProps) {
  const { language } = useLanguage();
  const { userRole } = useAuth();
  const locale = language === "es" ? es : enUS;
  const queryClient = useQueryClient();
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [movementType, setMovementType] = useState<"in" | "out" | "adjustment">("in");

  const { data: material, isLoading } = useQuery({
    queryKey: ["material-inventory", materialId],
    queryFn: async () => {
      const { data, error } = await api
        .from("material_inventory")
        .select("*")
        .eq("id", materialId)
        .single();

      if (error) throw error;
      return data;
    },
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  const { data: movements } = useQuery({
    queryKey: ["inventory-movements", materialId],
    queryFn: async () => {
      const { data, error } = await api
        .from("inventory_movements")
        .select(`
          *,
          profiles(full_name, email)
        `)
        .eq("material_inventory_id", materialId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as InventoryMovement[];
    },
  });

  const canManage = userRole === "admin" || userRole === "supervisor" || userRole === "warehouse";
  const canSeeCosts = ['admin', 'supervisor', 'plotter'].includes(userRole || '');

  const getMovementIcon = (type: string) => {
    switch (type) {
      case "in":
      case "entrada":
      case "purchase":
        return <ArrowUpCircle className="w-4 h-4 text-green-500" />;
      case "out":
      case "salida":
      case "consumption":
        return <ArrowDownCircle className="w-4 h-4 text-red-500" />;
      case "adjustment":
      case "ajuste":
        return <RotateCcw className="w-4 h-4 text-blue-500" />;
      case "transfer":
        return <Truck className="w-4 h-4 text-purple-500" />;
      default:
        return <History className="w-4 h-4 text-gray-500" />;
    }
  };

  const getMovementLabel = (type: string) => {
    const labels: Record<string, string> = {
      in: "Entrada",
      entrada: "Entrada",
      purchase: "Compra",
      out: "Salida",
      salida: "Salida",
      consumption: "Consumo",
      adjustment: "Ajuste",
      ajuste: "Ajuste",
      transfer: "Transferencia",
    };
    return labels[type] || type;
  };

  const getReasonLabel = (reason: string | null) => {
    if (!reason) return "-";
    const labels: Record<string, string> = {
      purchase: "Compra",
      production: "Produccion",
      return: "Devolucion",
      damage: "Danado",
      expired: "Vencido",
      correction: "Correccion",
      transfer_in: "Transferencia entrada",
      transfer_out: "Transferencia salida",
      initial: "Stock inicial",
    };
    return labels[reason] || reason;
  };

  const openMovementDialog = (type: "in" | "out" | "adjustment") => {
    setMovementType(type);
    setMovementDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!material) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Material no encontrado</p>
        <Button onClick={onBack} className="mt-4">
          Volver
        </Button>
      </div>
    );
  }

  const isLowStock = material.current_stock < material.min_stock;
  const stockPercentage = material.max_stock
    ? Math.min((material.current_stock / material.max_stock) * 100, 100)
    : null;
  const totalValue = (material.square_meters || 0) * (material.cost_per_unit || 0);
  const hasFreezerDetails = material.is_freezer_material && (
    material.roll_count != null ||
    material.batch_number ||
    material.roll_number ||
    material.production_date ||
    material.expiry_date ||
    material.storage_location ||
    material.storage_temperature ||
    material.storage_humidity ||
    material.safety_data_sheet_url
  );

  // Calculate stats from movements
  const last30DaysMovements = movements?.filter(
    (m) => new Date(m.created_at) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  ) || [];
  const totalIn = last30DaysMovements
    .filter((m) => ["in", "entrada", "purchase"].includes(m.movement_type))
    .reduce((sum, m) => sum + m.quantity, 0);
  const totalOut = last30DaysMovements
    .filter((m) => ["out", "salida", "consumption"].includes(m.movement_type))
    .reduce((sum, m) => sum + Math.abs(m.quantity), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{material.name}</h1>
            {material.code && (
              <Badge variant="outline">{material.code}</Badge>
            )}
            {!material.is_active && (
              <Badge variant="secondary">Inactivo</Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            {material.category || "Sin categoria"}
          </p>
        </div>
        {isLowStock && (
          <Badge className="bg-yellow-500/10 text-yellow-600 gap-1">
            <AlertTriangle className="w-4 h-4" />
            Stock Bajo
          </Badge>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stock Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Estado del Stock
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Stock Actual</p>
                  <p className={cn(
                    "text-3xl font-bold",
                    isLowStock ? "text-yellow-600" : "text-green-600"
                  )}>
                    {material.current_stock}
                  </p>
                  <p className="text-sm text-muted-foreground">{material.unit}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Stock Minimo</p>
                  <p className="text-3xl font-bold">{material.min_stock}</p>
                  <p className="text-sm text-muted-foreground">{material.unit}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Stock Maximo</p>
                  <p className="text-3xl font-bold">{material.max_stock || "-"}</p>
                  <p className="text-sm text-muted-foreground">{material.unit}</p>
                </div>
              </div>

              {stockPercentage !== null && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Nivel de stock</span>
                    <span>{stockPercentage.toFixed(0)}%</span>
                  </div>
                  <Progress
                    value={stockPercentage}
                    className={cn(
                      "h-3",
                      isLowStock && "[&>div]:bg-yellow-500",
                      stockPercentage > 80 && "[&>div]:bg-green-500"
                    )}
                  />
                </div>
              )}

              {canManage && (
                <div className="flex gap-2 mt-6">
                  <Button
                    onClick={() => openMovementDialog("in")}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Entrada
                  </Button>
                  <Button
                    onClick={() => openMovementDialog("out")}
                    variant="destructive"
                    className="flex-1"
                  >
                    <Minus className="w-4 h-4 mr-2" />
                    Salida
                  </Button>
                  <Button
                    onClick={() => openMovementDialog("adjustment")}
                    variant="outline"
                    className="flex-1"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Ajuste
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Movement History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historial de Movimientos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {movements && movements.length > 0 ? (
                <div className="space-y-3">
                  {movements.map((movement) => (
                    <div
                      key={movement.id}
                      className="flex items-center gap-4 p-3 rounded-lg border bg-muted/30"
                    >
                      {getMovementIcon(movement.movement_type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {getMovementLabel(movement.movement_type)}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {getReasonLabel(movement.reason)}
                          </Badge>
                        </div>
                        {movement.notes && (
                          <p className="text-sm text-muted-foreground truncate">
                            {movement.notes}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {movement.profiles?.full_name || "Sistema"} - {" "}
                          {format(new Date(movement.created_at), "PPp", { locale })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={cn(
                          "font-bold",
                          movement.quantity > 0 ? "text-green-600" : "text-red-600"
                        )}>
                          {movement.quantity > 0 ? "+" : ""}{movement.quantity} {material.unit}
                        </p>
                        {movement.previous_stock !== null && movement.new_stock !== null && (
                          <p className="text-xs text-muted-foreground">
                            {movement.previous_stock} → {movement.new_stock}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay movimientos registrados</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informacion</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {material.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{material.location}</span>
                </div>
              )}
              {material.supplier && (
                <div>
                  <p className="text-sm text-muted-foreground">Proveedor</p>
                  <p className="font-medium">{material.supplier}</p>
                </div>
              )}
              {material.material_type && (
                <div>
                  <p className="text-sm text-muted-foreground">Tipo</p>
                  <p className="font-medium">{material.material_type}</p>
                </div>
              )}
              {material.last_restock_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Ultimo restock</p>
                    <p className="font-medium">
                      {format(new Date(material.last_restock_date), "PP", { locale })}
                    </p>
                  </div>
                </div>
              )}
              {material.is_freezer_material && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary/70">
                    Zona de congeladores
                  </p>
                  {hasFreezerDetails ? (
                    <div className="space-y-1 text-sm">
                      {material.roll_count != null && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Bobinas/Rollos</span>
                          <span className="font-medium">{material.roll_count}</span>
                        </div>
                      )}
                      {material.batch_number && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Lote</span>
                          <span className="font-medium">{material.batch_number}</span>
                        </div>
                      )}
                      {material.roll_number && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Rollo</span>
                          <span className="font-medium">{material.roll_number}</span>
                        </div>
                      )}
                      {material.production_date && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Produccion</span>
                          <span className="font-medium">
                            {format(new Date(material.production_date), "PP", { locale })}
                          </span>
                        </div>
                      )}
                      {material.expiry_date && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Caducidad</span>
                          <span className="font-medium">
                            {format(new Date(material.expiry_date), "PP", { locale })}
                          </span>
                        </div>
                      )}
                      {material.storage_location && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Ubicación</span>
                          <span className="font-medium">{material.storage_location}</span>
                        </div>
                      )}
                      {material.storage_temperature && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Temperatura</span>
                          <span className="font-medium">{material.storage_temperature}</span>
                        </div>
                      )}
                      {material.storage_humidity && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Humedad</span>
                          <span className="font-medium">{material.storage_humidity}</span>
                        </div>
                      )}
                      {material.safety_data_sheet_url && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">SDS</span>
                          <a
                            href={material.safety_data_sheet_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline"
                          >
                            Ver
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Sin datos de congelador</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Value Card - Only visible to admin/supervisor/plotter */}
          {canSeeCosts && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Valoracion
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Costo por m²</span>
                  <span className="font-medium">
                    {material.cost_per_unit
                      ? `€${material.cost_per_unit.toFixed(2)}/m²`
                      : "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">m² en stock</span>
                  <span className="font-medium">
                    {(material.square_meters || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })} m²
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valor total</span>
                  <span className="font-bold text-lg">
                    €{totalValue.toLocaleString("es-ES", { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 30 Day Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Ultimos 30 dias</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <span className="text-muted-foreground">Entradas</span>
                </div>
                <span className="font-medium text-green-600">
                  +{totalIn} {material.unit}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-500" />
                  <span className="text-muted-foreground">Salidas</span>
                </div>
                <span className="font-medium text-red-600">
                  -{totalOut} {material.unit}
                </span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Movimientos</span>
                <span className="font-medium">{last30DaysMovements.length}</span>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {material.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notas</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{material.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <StockMovementDialog
        open={movementDialogOpen}
        onOpenChange={setMovementDialogOpen}
        materialId={materialId}
        materialName={material.name}
        currentStock={material.current_stock}
        unit={material.unit}
        movementType={movementType}
      />
    </div>
  );
}
